import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import * as api from '../../app/axios/api'
import { useRole } from '../../context/RoleContext'

import {
  Users,
  GraduationCap,
  DollarSign,
  TrendingUp,
  Award,
  UserCheck,
  AlertCircle,
  Plus,
  CheckCircle,
  Trash2,
  Calendar,
} from 'lucide-react'

const Home = () => {
  const navigate = useNavigate()
  const { role } = useRole()
  const [LeavePending, setLeavePending] = useState([])
  const [ExpensePending, setExpensePending] = useState([])
  const [dashboardDetails, setDashboardDetails] = useState({})
  const [fee, setFee] = useState([])
  const [expense, setExpense] = useState([])

  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1
  const currentYear = currentDate.getFullYear()
  const [month] = useState(currentMonth)
  const [year] = useState(currentYear)

  // --- API Fetchers ---
  const fetchDashBoardDetail = async () => {
    try {
      const res = await api.fetchDashBoardDetails()
      setDashboardDetails(res.data || {})
    } catch (error) {
      console.error(error)
    }
  }

  const fetchExpensePending = async () => {
    try {
      const res = await api.fetchDashBoardExpensePending()
      setExpensePending(res.data || [])
    } catch (error) {
      console.error(error)
    }
  }

  const fetchLeave = async () => {
    try {
      const res = await api.fetchDashBoardLeavePending()
      setLeavePending(res.data || [])
    } catch (error) {
      console.error(error)
    }
  }

  const fetchfee = async (m, y) => {
    try {
      const res = await api.fetchFeesDaysWise(m, y)
      setFee(res.data || [])
    } catch (error) {
      console.error(error)
    }
  }

  const fetchExpense = async (m, y) => {
    try {
      const res = await api.fetchToExpense(m, y)
      setExpense(res.data || [])
    } catch (error) {
      console.error(error)
    }
  }

  // --- Handle Approvals ---
  const handleApproveExpense = async (id) => {
    try {
      await api.updateExpanse(id, { is_approval: true })
      toast.success('Expense Approved')
      fetchExpensePending()
      fetchExpense(month, year)
    } catch (error) {
      toast.error('Failed to approve expense')
    }
  }

  const handleApproveLeave = async (id) => {
    try {
      await api.updateLeave(id, { is_approval: true })
      toast.success('Leave Approved')
      fetchLeave()
    } catch (error) {
      toast.error('Failed to approve leave')
    }
  }

  // --- Handle Deletions ---
  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return
    try {
      await api.deleteExpanse(id)
      toast.success('Expense Deleted')
      fetchExpensePending()
    } catch (error) {
      toast.error('Delete failed')
    }
  }

  const handleDeleteLeave = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave request?'))
      return
    try {
      await api.deleteLeave(id)
      toast.success('Leave Request Deleted')
      fetchLeave()
    } catch (error) {
      toast.error('Delete failed')
    }
  }

  useEffect(() => {
    fetchDashBoardDetail()
    fetchExpensePending()
    fetchfee(month, year)
    fetchLeave()
    fetchExpense(month, year)
  }, [month, year])

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  // Today's Stats Calculation
  const { totalRevenue, totalExpenses, profitLoss } = useMemo(() => {
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    const rev = fee.reduce((sum, item) => {
      const rawDate = item.fee_date || item.date || item.created_at
      let itemDateStr = rawDate?.includes('T') ? rawDate.split('T')[0] : rawDate
      return itemDateStr === todayStr
        ? sum + (parseFloat(item.total_collected) || 0)
        : sum
    }, 0)
    const exp = expense.reduce((sum, item) => {
      const rawDate = item.e_date || item.date || item.created_at
      let itemDateStr = rawDate?.includes('T') ? rawDate.split('T')[0] : rawDate
      return itemDateStr === todayStr
        ? sum + (parseFloat(item.total_expense) || 0)
        : sum
    }, 0)
    return { totalRevenue: rev, totalExpenses: exp, profitLoss: rev - exp }
  }, [fee, expense])

  return (
    <div className='space-y-6'>
      {/* Banner */}
      <div className='bg-gradient-to-r from-blue-700 to-indigo-600 p-8 rounded-2xl text-white shadow-lg'>
        <h1 className='text-3xl font-bold mb-2'>Welcome Back!</h1>
        <p className='text-blue-100 opacity-90 font-medium'>
          Inflection Org Pvt Ltd - Institute Management Dashboard
        </p>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        {[
          {
            title: 'Active Students',
            value: dashboardDetails.total_admission || 0,
            icon: Users,
            color: 'bg-blue-500',
            route: '/students',
          },
          {
            title: 'Active Batches',
            value: dashboardDetails.total_batch || 0,
            icon: GraduationCap,
            color: 'bg-green-500',
            route: '/batch',
          },
          {
            title: "Today's Attendance",
            value: dashboardDetails.total_attendance || 0,
            icon: UserCheck,
            color: 'bg-orange-500',
            route: '/attendance',
          },
          {
            title: 'Certificates',
            value: dashboardDetails.total_certificate || 0,
            icon: Award,
            color: 'bg-indigo-500',
            route: '/generateCertificate',
          },
        ].map((card, i) => (
          <div
            key={i}
            className='bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer group'
            onClick={() => card.route && navigate(card.route)}
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-600 mb-1'>
                  {card.title}
                </p>
                <p className='text-2xl font-bold text-gray-900'>{card.value}</p>
              </div>
              <div
                className={`${card.color} p-3 rounded-lg text-white group-hover:scale-110 transition-transform`}
              >
                <card.icon className='h-6 w-6' />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Financial Stats (Admin Only) */}

      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {[
          {
            title: "Today's Revenue",
            value: `₹${totalRevenue}`,
            icon: DollarSign,
            color: 'bg-purple-500',
          },
          {
            title: "Today's Expenses",
            value: `₹${totalExpenses}`,
            icon: TrendingUp,
            color: 'bg-yellow-500',
          },
          {
            title: profitLoss >= 0 ? "Today's Profit" : "Today's Loss",
            value: `₹${Math.abs(profitLoss)}`,
            icon: AlertCircle,
            color: profitLoss >= 0 ? 'bg-emerald-500' : 'bg-red-500',
          },
        ].map((card, i) => (
          <div
            key={i}
            className='bg-white p-5 rounded-xl shadow-sm flex items-center justify-between border border-gray-50'
          >
            <div>
              <p className='text-sm font-medium text-gray-500'>{card.title}</p>
              <p className='text-xl font-bold text-gray-900'>{card.value}</p>
            </div>
            <div className={`${card.color} p-2 rounded-lg text-white`}>
              <card.icon className='h-5 w-5' />
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className='bg-white p-6 rounded-xl shadow-sm'>
        <h2 className='text-lg font-bold text-gray-800 mb-4 flex items-center gap-2'>
          <Plus className='h-5 w-5 text-blue-600' /> Quick Actions
        </h2>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          <Link
            to='/studentRegistration'
            className='bg-blue-600 p-4 rounded-xl text-white hover:bg-blue-700 transition-all'
          >
            <Users className='h-6 w-6 mb-2' />
            <h3 className='font-semibold text-sm'>Add Student</h3>
          </Link>
          <Link
            to='/attendance'
            className='bg-emerald-600 p-4 rounded-xl text-white hover:bg-emerald-700 transition-all'
          >
            <UserCheck className='h-6 w-6 mb-2' />
            <h3 className='font-semibold text-sm'>Mark Attendance</h3>
          </Link>
          <Link
            to='/generateCertificate'
            className='bg-violet-600 p-4 rounded-xl text-white hover:bg-violet-700 transition-all'
          >
            <Award className='h-6 w-6 mb-2' />
            <h3 className='font-semibold text-sm'>Certificates</h3>
          </Link>
          <Link
            to='/expanse'
            className='bg-amber-600 p-4 rounded-xl text-white hover:bg-amber-700 transition-all'
          >
            <TrendingUp className='h-6 w-6 mb-2' />
            <h3 className='font-semibold text-sm'>Record Expense</h3>
          </Link>
        </div>
      </div>

      {/* --- PENDING APPROVALS SECTION --- */}
      {(role === 'admin' || role === 'manager' || role === 'director') && (
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
          {/* PENDING LEAVES */}
          <div className='bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden'>
            <div className='p-5 border-b bg-emerald-50/30 flex justify-between items-center'>
              <h2 className='text-lg font-bold text-gray-900 flex items-center gap-2'>
                <Calendar className='h-5 w-5 text-emerald-600' /> Pending Leaves
              </h2>
              <span className='bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-black'>
                {LeavePending.length} New
              </span>
            </div>
            <div className='max-h-[600px] overflow-y-auto p-5 space-y-4 custom-scrollbar bg-emerald-50/10'>
              {LeavePending.length > 0 ? (
                LeavePending.map((leave) => (
                  <div
                    key={leave.id}
                    className='bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:border-emerald-300 transition-all'
                  >
                    <div className='flex justify-between items-start mb-4 border-b border-gray-50 pb-3'>
                      <div>
                        <p className='text-[10px] font-bold text-gray-400 uppercase'>
                          Request ID: #{leave.id} • By: {leave.user_name}
                        </p>
                        <h4 className='font-black text-gray-800 text-sm'>
                          {formatDate(leave.leave_start_date)} to{' '}
                          {formatDate(leave.leave_end_date)}
                        </h4>
                      </div>
                      <div className='text-right'>
                        <p className='text-lg font-black text-emerald-600'>
                          {leave.total_number_of_leave_days_requested} Day(s)
                        </p>
                        <p className='text-[10px] text-gray-400 font-bold uppercase'>
                          Duration
                        </p>
                      </div>
                    </div>
                    {leave.half_leave?.valid && (
                      <div className='mb-3 p-2 bg-blue-50 text-blue-700 rounded text-[10px] font-bold'>
                        ⚠️ Half Leave: {leave.half_leave.h_leave}
                      </div>
                    )}
                    <div className='bg-gray-50 p-3 rounded-xl border border-gray-100 mb-4'>
                      <p className='text-[10px] text-gray-400 font-bold uppercase mb-1'>
                        Reason
                      </p>
                      <p className='text-xs text-gray-600 italic leading-relaxed'>
                        "{leave.reason_for_leave}"
                      </p>
                    </div>
                    <div className='flex gap-2'>
                      <button
                        onClick={() => handleApproveLeave(leave.id)}
                        className='flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 shadow-lg shadow-emerald-50 transition-all'
                      >
                        <CheckCircle className='h-4 w-4' /> APPROVE
                      </button>
                      <button
                        onClick={() => handleDeleteLeave(leave.id)}
                        className='px-4 bg-white border border-red-200 text-red-600 hover:bg-red-600 hover:text-white rounded-xl transition-all shadow-sm'
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className='text-center py-10 text-gray-400'>
                  <p className='text-xs font-bold uppercase tracking-widest'>
                    No Pending Leaves
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* PENDING EXPENSES */}
          <div className='bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden'>
            <div className='p-5 border-b border-gray-100 flex items-center justify-between bg-blue-50/30'>
              <h2 className='text-lg font-bold text-gray-900 flex items-center gap-2'>
                <DollarSign className='h-5 w-5 text-blue-600' /> Pending
                Expenses
              </h2>
              <span className='bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-black'>
                {ExpensePending.length} New
              </span>
            </div>
            <div className='max-h-[600px] overflow-y-auto p-5 space-y-4 custom-scrollbar bg-blue-50/10'>
              {ExpensePending.length > 0 ? (
                ExpensePending.map((exp) => (
                  <div
                    key={exp.id}
                    className='bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:border-blue-300 transition-all'
                  >
                    <div className='flex items-center justify-between gap-4 mb-4 border-b border-gray-50 pb-3'>
                      <div className='flex items-center gap-3'>
                        <div className='h-10 w-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center font-black text-xs uppercase'>
                          {exp.category?.slice(0, 2)}
                        </div>
                        <div>
                          <h4 className='font-black text-gray-900 uppercase tracking-tight'>
                            {exp.for_product}
                          </h4>
                          <p className='text-[10px] text-gray-500 font-bold uppercase'>
                            {exp.user_name || 'Staff'}
                          </p>
                        </div>
                      </div>
                      <p className='text-xl font-black text-blue-600'>
                        ₹{exp.amount}
                      </p>
                    </div>
                    <div className='grid grid-cols-2 gap-4 mb-4'>
                      <div>
                        <p className='text-[10px] text-gray-400 font-bold uppercase'>
                          Vendor / Mode
                        </p>
                        <p className='text-xs font-bold text-gray-700'>
                          {exp.purchase_from} • {exp.payment_mode}
                        </p>
                      </div>
                      <div className='text-right'>
                        <p className='text-[10px] text-gray-400 font-bold uppercase'>
                          Date
                        </p>
                        <p className='text-xs font-bold text-gray-700'>
                          {formatDate(exp.expense_date)}
                        </p>
                      </div>
                    </div>
                    <div className='flex gap-2'>
                      <button
                        onClick={() => handleApproveExpense(exp.id)}
                        className='flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 shadow-lg shadow-blue-50 transition-all'
                      >
                        <CheckCircle className='h-4 w-4' /> APPROVE
                      </button>
                      <button
                        onClick={() => handleDeleteExpense(exp.id)}
                        className='px-4 bg-white border border-red-200 text-red-600 hover:bg-red-600 hover:text-white rounded-xl transition-all shadow-sm'
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className='text-center py-10 text-gray-400'>
                  <p className='text-xs font-bold uppercase tracking-widest'>
                    No Pending Expenses
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
